"use client"

import { useState } from "react"
import Image from "next/image"
import { X } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/lib/auth-store"
import { markSeen } from "@/lib/client/seen-state"
import { useTranslations } from "next-intl"
import { useModalContent } from "@/lib/modal-content"
import { STANDARD_DAILY_GOONCOIN } from "@/lib/glymtober"
import { DEFAULT_HOLIDAY_EVENTS, holidayPictureFor, holidaySeenKey, localHolidaySeenKey, type HolidayEvent, type HolidayId } from "@/lib/holiday-events"

const SLOT: Record<HolidayId, string> = {
  glymsgiving: "glymsgiving_welcome",
  glymus: "glymus_welcome",
  new_years: "new_years_welcome",
}

// Title colour and button colour per event. The layout is the Glymtober card's, so the popups feel like one family.
// borderFilter turns the purple goop frame another colour. Glymus gets a green frame so the red title and button are not fighting purple;
// Glymsgiving and New Year's keep the purple frame (owner, 2026-10-27). For a red frame use hue-rotate(95deg).
const ACCENT: Record<HolidayId, { title: string; highlight: string; button: string; glow: string; borderFilter?: string }> = {
  glymsgiving: { title: "text-orange-400", highlight: "text-orange-400", button: "bg-orange-500 hover:bg-orange-400", glow: "shadow-[0_0_16px_rgba(249,115,22,0.4)]" },
  glymus: { title: "text-red-400", highlight: "text-green-400", button: "bg-red-600 hover:bg-red-500", glow: "shadow-[0_0_16px_rgba(239,68,68,0.45)]", borderFilter: "hue-rotate(-125deg) saturate(1.15)" },
  new_years: { title: "text-amber-300", highlight: "text-amber-300", button: "bg-amber-500 hover:bg-amber-400", glow: "shadow-[0_0_16px_rgba(245,158,11,0.45)]" },
}

/** The welcome popup for one holiday event (Glymsgiving, Glymus, New Year's). Same job and layout as GlymtoberCard: day-one-promo-stack.tsx
 * decides when it shows; this only draws it. The picture depends on the date (lib/holiday-events.ts), unless an admin uploaded their own
 * art for the slot, which then replaces all of them. Shown once per holiday per year. */
export function HolidayCard({ event, onDone, onReady, date }: { event: HolidayEvent; onDone: () => void; onReady?: () => void; date?: Date }) {
  const t = useTranslations("holidayModal")
  const tg = useTranslations("glymtoberModal")
  const content = useModalContent(SLOT[event.id], true)
  const [heroLoaded, setHeroLoaded] = useState(true)
  const { user } = useAuthStore()
  const today = date ?? new Date()
  const accent = ACCENT[event.id]

  const dismiss = () => {
    if (user) {
      void markSeen(holidaySeenKey(event, today))
    } else {
      try { localStorage.setItem(localHolidaySeenKey(event, today), "1") } catch {}
    }
    onDone()
  }

  if (!heroLoaded || !content.ready) return null

  const title = t(`${event.id}Title` as "glymsgivingTitle" | "glymusTitle" | "new_yearsTitle")
  const overridden = content.hasArtOverride("hero")
  const picture = holidayPictureFor(event, today)
  const hero = overridden ? content.art("hero") : picture?.src ?? ""
  // The spinning Gooncoin sits on the spot set for this picture; an uploaded override has no spot, so no coin.
  const coin = overridden ? undefined : picture?.coin
  const boostTimes = Math.max(1, Math.round(event.dailyGooncoin / STANDARD_DAILY_GOONCOIN))
  const bodyPre = event.id === "new_years" ? t("bodyPreNewYears") : t("bodyPreMonth")

  return (
    // The holiday pictures are 941x1672 (9:16), the same shape as this frame, so nothing is cropped or stretched.
    <div className="relative w-full aspect-[9/16]">
      <div className="absolute inset-0 rounded-2xl overflow-hidden bg-background shadow-2xl">
        {/* Plain <img>: an uploaded override lives on the storage host, which next/image would need whitelisting for. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          key={hero}
          src={hero}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          ref={(el) => { if (el && el.complete && el.naturalWidth > 0) onReady?.() }}
          onLoad={() => onReady?.()}
          onError={() => { setHeroLoaded(false); onReady?.() }}
        />

        {/* Spinning Gooncoin, placed per picture (lib/holiday-events.ts). Same asset and glow as the Glymtober card. */}
        {coin && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src="/gooncoin/gooncoin_spin.gif"
            alt=""
            className="absolute aspect-square pointer-events-none [filter:brightness(1.15)_drop-shadow(0_4px_10px_rgba(0,0,0,0.5))_drop-shadow(0_0_18px_rgba(196,165,255,0.85))]"
            style={{ left: coin.left + "%", top: coin.top + "%", width: coin.width + "%" }}
          />
        )}

        {/* Shadow rising behind the text and button, so they read on the bright pictures too (the snowman is mostly white). */}
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            top: "48%",
            background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.9) 62%, rgba(0,0,0,0.95) 100%)",
          }}
        />

        <div className="absolute inset-x-0 bottom-0 px-7 pb-6 pt-3 flex flex-col gap-6">
          <div className="space-y-2 text-center">
            <p className={`text-2xl font-bold ${accent.title} [text-shadow:_0_2px_10px_rgb(0_0_0_/_90%),_0_1px_3px_rgb(0_0_0_/_100%)]`}>
              {content.text("title", title)}
            </p>
            <p className="text-base text-white/90 leading-relaxed [text-shadow:_0_2px_8px_rgb(0_0_0_/_85%),_0_1px_2px_rgb(0_0_0_/_100%)]">
              {content.text("body_pre", bodyPre)}{" "}
              <span className={`font-bold ${accent.highlight}`}>
                {content.text("body_stat", tg("amountGooncoinADay", { amount: event.dailyGooncoin }), { amount: String(event.dailyGooncoin) })}
              </span>{" "}
              {content.text("body_post", tg("timesTheUsualAmount", { times: boostTimes }), { times: String(boostTimes) })}
            </p>
          </div>

          <Button onClick={dismiss} className={`w-full h-12 rounded-full text-base ${accent.button} text-white border-0 ${accent.glow}`}>
            {content.text("button", tg("letSGo"))}
          </Button>
        </div>
      </div>

      <button
        onClick={dismiss}
        aria-label={t("close")}
        className="absolute top-3 right-3 z-30 h-8 w-8 rounded-full bg-black/70 backdrop-blur-md border border-white/25 flex items-center justify-center text-white opacity-90 hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>

      {/* The same goop frame as the Glymtober card, overscanning slightly so it wraps around the card. */}
      <div className="absolute z-20 pointer-events-none" style={{ inset: "-3% -3%", filter: accent.borderFilter }}>
        <Image src="/borders/goopborder-slime-portrait.webp" alt="" fill style={{ objectFit: "fill" }} unoptimized />
      </div>
    </div>
  )
}

/** Admin preview of a holiday popup (Admin > Modal Content > Preview): shows the card immediately, with no gating of its own. */
export function HolidayModalPreview({ eventId, onClose }: { eventId: HolidayId; onClose?: () => void }) {
  const event = DEFAULT_HOLIDAY_EVENTS.find((e) => e.id === eventId)
  if (!event) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-sm">
        <HolidayCard event={event} onDone={() => onClose?.()} />
      </div>
    </div>
  )
}
